import { cn } from "@/lib/utils";
import { UniqueIdentifier } from "@dnd-kit/core";
import { ExtensionLink } from "components/core/ExtensionLink";
import { IconBox } from "components/core/IconBox";
import { ToggleButton } from "components/core/buttons/ToggleButton";
import { AppContext } from "contexts/AppContext";
import {
    GetUserInterestTagsDocument,
    useDeleteUserInterestTagsMutation,
    useGetUserInterestTagsQuery,
    useSessionQuery,
    useUpsertUserInterestTagsMutation
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import { AppPage } from "models/AppPage";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    DEVICE_TYPE,
    MIX_CONTENT_TAG_ENUM_KOREAN,
    ORDERED_MIX_CONTENT_TAG_ENUM,
    SHINHAN_INVEST_TAG_LIST,
    TRIAL_TAG_LIST
} from "utils/enum";

import { Tooltip } from "../Tooltip";

// DndContainer를 클라이언트 사이드에서만 로드
const DndContainer = dynamic(
    () =>
        import("components/core/dnd/DndContainer").then((mod) => ({
            default: mod.DndContainer
        })),
    {
        ssr: false,
        loading: () => <div>Loading...</div>
    }
);

export const NavMenu = ({
    showSubMenu,
    className,
    ...props
}: {
    showSubMenu: boolean;
    className?: string;
}) => {
    const router = useRouter();
    const { me, isLoggedIn } = useAuth();
    const { data: sessionData } = useSessionQuery();
    const userId = sessionData?.session?.me?.id;

    const { deviceType } = useContext(AppContext);

    const { data: userInterestTagsData } = useGetUserInterestTagsQuery({
        variables: { userId: userId! },
        skip: !userId
    });

    const [upsertUserInterestTags] = useUpsertUserInterestTagsMutation();
    const [deleteUserInterestTags] = useDeleteUserInterestTagsMutation();

    // 서브메뉴 순서 상태 관리
    const [subMenuOrders, setSubMenuOrders] = useState<
        Record<string, string[]>
    >({});

    // 클라이언트 사이드 체크
    const [isClient, setIsClient] = useState(false);

    // 클라이언트 사이드 마운트 체크
    useEffect(() => {
        setIsClient(true);
    }, []);

    const displayTags = useMemo(() => {
        const userGroup = sessionData?.session?.me?.group_id;
        const userInterestTags = userInterestTagsData?.mix_user_interest_tag;

        // priority 순서로 정렬된 tag values
        const userTagValues = userInterestTags
            ?.slice() // 원본 배열 복사
            ?.sort((a, b) => a.priority - b.priority)
            ?.map((item) => item.tag_value);

        // 사용자가 선택한 태그가 있는 경우
        if (isLoggedIn && userTagValues && userTagValues.length > 0) {
            // 시작: 사용자가 선택한 태그를 기반으로 필터링
            let filtered = [...userTagValues];

            // 신한투자증권 사용자가 아닌 경우, 신한투자증권 태그 제외
            if (userGroup !== "shinhaninvest") {
                filtered = filtered.filter(
                    (tag) =>
                        !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag)
                );
            }

            // teammint 사용자가 아닌 경우, trial 전용 태그 제외
            if (userGroup !== "teammint") {
                filtered = filtered.filter(
                    (tag) => !(TRIAL_TAG_LIST as string[]).includes(tag)
                );
            }

            return filtered;
        }

        // 사용자가 선택한 태그가 없는 경우 기본 태그 세트 구성
        let base =
            userGroup === "shinhaninvest"
                ? [...ORDERED_MIX_CONTENT_TAG_ENUM]
                : ORDERED_MIX_CONTENT_TAG_ENUM.filter(
                      (tag) =>
                          !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag)
                  );

        // teammint 사용자가 아닌 경우, trial 전용 태그 제외
        if (userGroup !== "teammint") {
            base = base.filter(
                (tag) => !(TRIAL_TAG_LIST as string[]).includes(tag as string)
            );
        }

        return base;
    }, [isLoggedIn, userInterestTagsData, sessionData]);

    const pages: Array<AppPage> = useMemo(
        () => [
            new AppPage({
                href: "/",
                label: "콘텐츠 큐레이션",
                external: false,
                iconName: "All",
                hasRightButton: true,
                hasHoverTooltip: router.pathname === "/" ? true : false,
                tooltipContent: <>관심 주제 설정</>,
                children: displayTags
                    .map((value) => {
                        const label =
                            MIX_CONTENT_TAG_ENUM_KOREAN[
                                value as keyof typeof MIX_CONTENT_TAG_ENUM_KOREAN
                            ];
                        if (!label) return null;

                        return new AppPage({
                            href: `/?tag=${value}`,
                            label,
                            external: false
                        });
                    })
                    .filter((page): page is AppPage => page !== null)
            }),
            new AppPage({
                href: "/original",
                label: "오리지널 콘텐츠",
                external: false,
                iconName: "Original"
            }),
            new AppPage({
                href: "/archive",
                label: "아카이브 (Beta)",
                external: false,
                iconName: "Folder",
                hasHoverTooltip: true,
                tooltipContent: (
                    <>
                        아카이브에서는 마케터를 위한 다양한 자료를 모아
                        제공합니다. 현재는 광고 상품 소개서 중심으로
                        <br />
                        제공하며, 트렌드 인사이트, 실무 템플릿 등 점차 다양한
                        자료로 확대해 나갈 예정입니다.
                        <br />
                        <br />
                        <span className="italic">(로그인이 필요합니다.)</span>
                    </>
                )
            }),
            new AppPage({
                href: "/bookmark",
                label: "북마크",
                external: false,
                iconName: "BookmarkFilled"
            }),
            new AppPage({
                href: "https://support.mix.day",
                label: "고객센터",
                external: true,
                iconName: "InfoFilled"
            })
        ],
        [displayTags, router.pathname]
    );

    const pagesInMypage: Array<AppPage> = useMemo(
        () => [
            new AppPage({
                href: "/mypage",
                label: "나의 정보",
                external: false,
                iconName: "AccountCircle"
            }),
            new AppPage({
                href: "/mypage/orders",
                label: "나의 주문 내역",
                external: false,
                iconName: "List"
            }),
            ...(me?.has_lms_access
                ? [
                      new AppPage({
                          href: "/mypage/courses",
                          label: "나의 강의실",
                          external: false,
                          iconName: "Class"
                      })
                  ]
                : [])
        ],
        [me?.has_lms_access]
    );

    const currentPages = useMemo(
        () => (router.pathname.startsWith("/mypage") ? pagesInMypage : pages),
        [router.pathname, pagesInMypage, pages]
    );

    // 각 페이지의 children 순서 초기화 - displayTags가 변경될 때만 초기화
    useEffect(() => {
        const newOrders: Record<string, string[]> = {};
        currentPages.forEach((page) => {
            if (page.children && page.children.length > 0) {
                const pageKey = page.href;
                // 기존 subMenuOrders가 없거나 displayTags가 변경된 경우에만 초기화
                if (!subMenuOrders[pageKey]) {
                    newOrders[pageKey] = page.children.map(
                        (child) => child.href
                    );
                }
            }
        });

        if (Object.keys(newOrders).length > 0) {
            setSubMenuOrders((prev) => ({ ...prev, ...newOrders }));
        }
    }, [displayTags]);

    // priority 변경사항을 subMenuOrders에 반영 (displayTags 변경 시에만)
    useEffect(() => {
        if (isLoggedIn && userInterestTagsData?.mix_user_interest_tag) {
            const pageKey = "/";
            const sortedTags = [...userInterestTagsData.mix_user_interest_tag]
                .sort((a, b) => a.priority - b.priority)
                .map((tag) => `/?tag=${tag.tag_value}`);

            setSubMenuOrders((prev) => ({
                ...prev,
                [pageKey]: sortedTags
            }));
        }
    }, [userInterestTagsData, isLoggedIn]);

    // 태그가 신한투자증권 태그인지 확인하는 함수
    const isShinhanInvestTag = (href: string): boolean => {
        const match = href.match(/\?tag=(.+)$/);
        if (!match || !match[1]) return false;
        const tagValue = match[1];
        return (SHINHAN_INVEST_TAG_LIST as string[]).includes(tagValue);
    };

    // 드래그 앤 드롭 완료 핸들러
    const handleSubMenuDragEnd = async (
        pageHref: string,
        newOrder: UniqueIdentifier[]
    ) => {
        // Optimistic UI update - 즉시 UI 업데이트
        setSubMenuOrders((prev) => ({
            ...prev,
            [pageHref]: newOrder as string[]
        }));

        // 콘텐츠 큐레이션 페이지의 서브메뉴인 경우만 priority 업데이트
        if (
            pageHref === "/" &&
            userId &&
            userInterestTagsData?.mix_user_interest_tag
        ) {
            // newOrder에서 tag value만 추출 (/?tag=value 형식에서 value 추출)
            const reorderedTagValues = newOrder
                .map((href) => {
                    const match = href.toString().match(/\?tag=(.+)$/);
                    return match ? match[1] : null;
                })
                .filter((value): value is string => value !== null);

            // 현재 사용자의 모든 interest tags 가져오기
            const currentUserTags = [
                ...userInterestTagsData.mix_user_interest_tag
            ];

            // 드래그한 태그들을 새 순서대로 정렬하고, 나머지 태그들도 포함
            const allTagsMap = new Map(
                currentUserTags.map((tag) => [tag.tag_value, tag])
            );

            // 새로운 priority 할당: 드래그된 태그들 먼저, 그 다음 나머지 태그들
            const objects = reorderedTagValues.map((tag_value, index) => ({
                user_id: userId,
                tag_value: tag_value as any, // GraphQL enum 타입 강제 변환
                priority: index + 1 // 1-based index
            }));

            // 드래그되지 않은 나머지 태그들 추가
            let nextPriority = reorderedTagValues.length + 1;
            currentUserTags.forEach((tag) => {
                if (!reorderedTagValues.includes(tag.tag_value)) {
                    objects.push({
                        user_id: userId,
                        tag_value: tag.tag_value as any, // GraphQL enum 타입 강제 변환
                        priority: nextPriority++
                    });
                }
            });

            try {
                // 1단계: 모든 기존 태그를 임시 priority로 업데이트 (uniqueness 충돌 방지)
                const tempObjects = currentUserTags.map((tag, index) => ({
                    user_id: userId,
                    tag_value: tag.tag_value as any,
                    priority: 1000 + index // 임시로 높은 값 사용
                }));

                await upsertUserInterestTags({
                    variables: { objects: tempObjects }
                });

                // 2단계: 최종 priority로 업데이트
                await upsertUserInterestTags({
                    variables: { objects }
                });
            } catch (error) {
                console.error(
                    "Failed to update interest tag priorities:",
                    error
                );
                // 에러 발생 시에도 optimistic update는 유지 (사용자 경험 우선)
            }
        }
    };

    return (
        <div
            className={cn(
                "mt-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto",
                `[-ms-overflow-style]:none [scrollbar-width]:none
                [&::-webkit-scrollbar]:hidden`,
                className
            )}
            {...props}
        >
            {currentPages.map((page) => {
                const isPageActive = page.active(router.pathname);
                return (
                    <div key={page.href}>
                        {page.external ? (
                            page.hasHoverTooltip ? (
                                <Tooltip
                                    placement="top"
                                    trigger={
                                        deviceType !== DEVICE_TYPE.DESKTOP
                                            ? "mousedown"
                                            : "mouseover"
                                    }
                                >
                                    <Tooltip.Trigger asChild={false}>
                                        <a
                                            key={page.href}
                                            href={page.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ToggleButton
                                                name={page.iconName}
                                                active={page.active(
                                                    router.pathname
                                                )}
                                            >
                                                {page.label}
                                            </ToggleButton>
                                        </a>
                                    </Tooltip.Trigger>
                                    <Tooltip.Content
                                        className="-ml-1 max-w-[260px]
                                            break-keep rounded-md px-[10px]
                                            pb-0.5 pt-2 text-xs"
                                        backgroundColor="#eeeeee"
                                        color="#121212"
                                    >
                                        <div className="mb-1.5 font-medium">
                                            {page.tooltipContent}
                                        </div>
                                    </Tooltip.Content>
                                </Tooltip>
                            ) : (
                                <a
                                    key={page.href}
                                    href={page.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ToggleButton
                                        name={page.iconName}
                                        active={page.active(router.pathname)}
                                    >
                                        {page.label}
                                    </ToggleButton>
                                </a>
                            )
                        ) : (
                            <ExtensionLink href={page.href}>
                                <Tooltip
                                    placement="top"
                                    trigger={
                                        deviceType !== DEVICE_TYPE.DESKTOP
                                            ? "mousedown"
                                            : "mouseover"
                                    }
                                >
                                    <Tooltip.Trigger asChild={false}>
                                        <ToggleButton
                                            name={page.iconName}
                                            rightIcon={
                                                page.hasRightButton &&
                                                isPageActive
                                                    ? "MoreHoriz"
                                                    : null
                                            }
                                            active={page.active(
                                                router.pathname
                                            )}
                                            onRightIconClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const hash = me
                                                    ? "#interest-tags"
                                                    : "#signin";
                                                if (
                                                    process.env.BUILD_TARGET ===
                                                    "extension"
                                                ) {
                                                    router.push(
                                                        `https://mix.day/${hash}`
                                                    );
                                                } else {
                                                    router.push(
                                                        {
                                                            ...router,
                                                            hash
                                                        },
                                                        undefined,
                                                        { scroll: false }
                                                    );
                                                }
                                            }}
                                        >
                                            {page.label}
                                        </ToggleButton>
                                    </Tooltip.Trigger>
                                    {((page.hasRightButton && isPageActive) ||
                                        page.hasHoverTooltip) && (
                                        <Tooltip.Content
                                            className={cn(
                                                `max-w-[260px] break-keep
                                                    rounded-md px-[10px] pb-0.5
                                                    pt-2 text-xs`,
                                                page.hasRightButton
                                                    ? "ml-16"
                                                    : "-ml-1"
                                            )}
                                            backgroundColor="#eeeeee"
                                            color="#121212"
                                        >
                                            <div className="mb-1.5 font-medium">
                                                {page.tooltipContent}
                                            </div>
                                        </Tooltip.Content>
                                    )}
                                </Tooltip>
                            </ExtensionLink>
                        )}
                        {showSubMenu &&
                            page.children &&
                            page.children.length > 0 && (
                                <div
                                    className={cn(
                                        `flex flex-col gap-1 overflow-hidden
                                        transition-all duration-200`,
                                        isPageActive
                                            ? "py-2 ease-in"
                                            : "max-h-0 p-0 ease-out"
                                    )}
                                    style={
                                        isPageActive
                                            ? {
                                                  maxHeight: `${
                                                      page.children.length * 30
                                                  }px`
                                              }
                                            : { maxHeight: "0" }
                                    }
                                >
                                    {isClient && isLoggedIn ? (
                                        <DndContainer
                                            onDragEnd={(newOrder) =>
                                                handleSubMenuDragEnd(
                                                    page.href,
                                                    newOrder
                                                )
                                            }
                                            renderItems={(() => {
                                                const pageOrder =
                                                    subMenuOrders[page.href] ||
                                                    page.children?.map(
                                                        (child) => child.href
                                                    ) ||
                                                    [];
                                                const orderedChildren =
                                                    pageOrder
                                                        .map((href) =>
                                                            page.children?.find(
                                                                (child) =>
                                                                    child.href ===
                                                                    href
                                                            )
                                                        )
                                                        .filter(
                                                            (
                                                                child
                                                            ): child is NonNullable<
                                                                typeof child
                                                            > =>
                                                                child !==
                                                                undefined
                                                        );

                                                return orderedChildren.map(
                                                    (child) => (
                                                        <ExtensionLink
                                                            key={child.href}
                                                            href={child.href}
                                                        >
                                                            <div
                                                                className={cn(
                                                                    "relative flex cursor-pointer items-center overflow-hidden whitespace-nowrap py-0.5 pl-6 font-['Pretendard'] text-sm font-normal leading-[140%]",
                                                                    child.active(
                                                                        router.asPath
                                                                    )
                                                                        ? "font-bold text-secondary-main"
                                                                        : "text-text-disabled",
                                                                    "group hover:text-secondary-main"
                                                                )}
                                                                onClick={() => {
                                                                    fireEvent({
                                                                        event: "content_category_click",
                                                                        params: {
                                                                            value: child.label
                                                                        }
                                                                    });
                                                                }}
                                                            >
                                                                <IconBox
                                                                    className="absolute left-6 cursor-grab opacity-0 transition-opacity duration-200 active:cursor-grabbing group-hover:opacity-100"
                                                                    name="Menu"
                                                                    size="small"
                                                                    color="grey"
                                                                />
                                                                <span
                                                                    className={cn(
                                                                        "flex items-center gap-1 pl-6",
                                                                        isShinhanInvestTag(
                                                                            child.href
                                                                        ) &&
                                                                            "text-[#0246ff]"
                                                                    )}
                                                                >
                                                                    {isShinhanInvestTag(
                                                                        child.href
                                                                    ) && (
                                                                        <Image
                                                                            src="/img/logo/shinhaninvest.png"
                                                                            alt="shinhaninvest"
                                                                            width={
                                                                                16
                                                                            }
                                                                            height={
                                                                                16
                                                                            }
                                                                        />
                                                                    )}
                                                                    {
                                                                        child.label
                                                                    }
                                                                </span>
                                                            </div>
                                                        </ExtensionLink>
                                                    )
                                                );
                                            })()}
                                        />
                                    ) : (
                                        // SSR 또는 비로그인 사용자는 기본 리스트 렌더링
                                        <>
                                            {page.children.map((child) => (
                                                <ExtensionLink
                                                    key={child.href}
                                                    href={child.href}
                                                >
                                                    <div
                                                        className={cn(
                                                            `relative flex
                                                                cursor-pointer
                                                                items-center
                                                                overflow-hidden
                                                                whitespace-nowrap
                                                                py-0.5 pl-6
                                                                font-['Pretendard']
                                                                text-sm
                                                                font-normal
                                                                leading-[140%]`,
                                                            child.active(
                                                                router.asPath
                                                            )
                                                                ? `font-bold
                                                                    text-secondary-main`
                                                                : "text-text-disabled",
                                                            "hover:text-secondary-main"
                                                        )}
                                                        onClick={() => {
                                                            fireEvent({
                                                                event: "content_category_click",
                                                                params: {
                                                                    value: child.label
                                                                }
                                                            });
                                                        }}
                                                    >
                                                        <span
                                                            className="flex
                                                                items-center
                                                                gap-2 pl-6"
                                                        >
                                                            {isShinhanInvestTag(
                                                                child.href
                                                            ) && (
                                                                <Image
                                                                    src="/img/logo/shinhaninvest.png"
                                                                    alt="shinhaninvest"
                                                                    width={16}
                                                                    height={16}
                                                                />
                                                            )}
                                                            {child.label}
                                                        </span>
                                                    </div>
                                                </ExtensionLink>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                    </div>
                );
            })}
        </div>
    );
};
